// /client/src/pages/Admin/DashboardAdherents/CourseStatisticsTable.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getGroupStats } from "../../../services/dashboardServices";
import {
  FaSearch,
  FaFilter,
  FaSortAmountUpAlt,
  FaSortAmountDownAlt,
  FaUndoAlt,
} from "react-icons/fa";

// Définition des colonnes/clefs renvoyées par l'API + libellés + couleurs
const STATUS_COLUMNS = [
  {
    key: "Licencie",
    label: "Licencié Club",
    pill: "bg-emerald-500",
    ring: "ring-emerald-300/40",
  },
  {
    key: "LicencieHorsClub",
    label: "Licencié Hors Club",
    pill: "bg-emerald-300",
    ring: "ring-emerald-100/40",
  },
  {
    key: "licenceetarret",
    label: "Licencié - Arrêt",
    pill: "bg-slate-400",
    ring: "ring-slate-300/40",
  },
  {
    key: "inscrit",
    label: "Inscrit",
    pill: "bg-amber-500",
    ring: "ring-amber-300/40",
  },
  {
    key: "courssessai",
    label: "Cours d’essai",
    pill: "bg-rose-500",
    ring: "ring-rose-300/40",
  },
  {
    key: "AnciensLicencieArrêt",
    label: "Ancien Licencié & Arrêt",
    pill: "bg-slate-600",
    ring: "ring-slate-400/40",
  },
];

export default function CourseStatisticsTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtres & tri
  const [query, setQuery] = useState("");
  const [activeKeys, setActiveKeys] = useState(
    () => new Set(STATUS_COLUMNS.map((c) => c.key))
  );
  const [sortBy, setSortBy] = useState("total"); // 'total' | 'name'
  const [sortDir, setSortDir] = useState("desc"); // 'asc' | 'desc'

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await getGroupStats();
        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Erreur lors de la récupération des données", e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Helpers
  const isActive = (key) => activeKeys.has(key);
  const toggleKey = (key) =>
    setActiveKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const selectAll = () =>
    setActiveKeys(new Set(STATUS_COLUMNS.map((c) => c.key)));
  const selectNone = () => setActiveKeys(new Set());

  const filterQuery = query.trim().toLowerCase();

  // Lignes filtrées + total par ligne (en fonction des colonnes actives)
  const filteredRows = useMemo(() => {
    const base = rows
      .map((r) => {
        const totalSelected = STATUS_COLUMNS.reduce((sum, col) => {
          if (!activeKeys.has(col.key)) return sum;
          const v = Number(r[col.key] ?? 0);
          return sum + (isNaN(v) ? 0 : v);
        }, 0);
        return { ...r, _totalSelected: totalSelected };
      })
      .filter((r) =>
        filterQuery
          ? String(r?.nom || "")
              .toLowerCase()
              .includes(filterQuery)
          : true
      );

    base.sort((a, b) => {
      if (sortBy === "name") {
        const va = String(a?.nom || "").toLowerCase();
        const vb = String(b?.nom || "").toLowerCase();
        return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      // sortBy === 'total'
      const va = Number(a._totalSelected || 0);
      const vb = Number(b._totalSelected || 0);
      return sortDir === "asc" ? va - vb : vb - va;
    });

    return base;
  }, [rows, activeKeys, filterQuery, sortBy, sortDir]);

  // Totaux de colonnes (selon filtres actifs)
  const columnTotals = useMemo(() => {
    const obj = {};
    STATUS_COLUMNS.forEach(({ key }) => (obj[key] = 0));
    for (const r of filteredRows) {
      STATUS_COLUMNS.forEach(({ key }) => {
        obj[key] += Number(r[key] ?? 0);
      });
    }
    return obj;
  }, [filteredRows]);

  const totalSelectedAllRows = useMemo(
    () => filteredRows.reduce((s, r) => s + Number(r._totalSelected || 0), 0),
    [filteredRows]
  );

  const n = (x) => new Intl.NumberFormat("fr-FR").format(Number(x || 0));

  return (
    <section className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur">
      <div className="max-h-[85vh] overflow-auto">
        {/* En-tête */}
        <div className="px-5 pt-5 sm:px-6">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
            Statistiques par cours
          </h3>
          <div className="mt-2 h-1 w-28 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
        </div>

        {/* Barre d’outils */}
        <div className="px-5 sm:px-6 mt-4">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
            {/* Recherche */}
            <div className="xl:col-span-1">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher un cours…"
                  className="w-full rounded-xl pl-9 pr-3 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
            </div>

            {/* Tri */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600 dark:text-slate-400 hidden sm:inline">
                Tri :
              </span>
              <div className="inline-flex rounded-xl overflow-hidden ring-1 ring-slate-300 dark:ring-slate-700">
                <button
                  className={`px-3 py-2 text-sm ${
                    sortBy === "name"
                      ? "bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                      : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300"
                  }`}
                  onClick={() => setSortBy("name")}
                >
                  Nom
                </button>
                <button
                  className={`px-3 py-2 text-sm ${
                    sortBy === "total"
                      ? "bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                      : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300"
                  }`}
                  onClick={() => setSortBy("total")}
                >
                  Total sélection
                </button>
              </div>
              <button
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 ring-1 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 ring-slate-300 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                onClick={() =>
                  setSortDir((d) => (d === "asc" ? "desc" : "asc"))
                }
                title={`Ordre ${
                  sortDir === "asc" ? "croissant" : "décroissant"
                }`}
              >
                {sortDir === "asc" ? (
                  <FaSortAmountUpAlt />
                ) : (
                  <FaSortAmountDownAlt />
                )}
                {sortDir === "asc" ? "Croissant" : "Décroissant"}
              </button>
            </div>

            {/* Filtres colonnes */}
            <div className="xl:col-span-1">
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                  <FaFilter /> Statuts :
                </span>
                <button
                  onClick={selectAll}
                  className="text-xs rounded-lg px-2 py-1 ring-1 ring-slate-300 dark:ring-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800"
                  title="Tout sélectionner"
                >
                  Tout
                </button>
                <button
                  onClick={selectNone}
                  className="text-xs rounded-lg px-2 py-1 ring-1 ring-slate-300 dark:ring-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800"
                  title="Désélectionner"
                >
                  Aucun
                </button>

                {STATUS_COLUMNS.map(({ key, label, pill, ring }) => {
                  const active = isActive(key);
                  return (
                    <button
                      key={key}
                      onClick={() => toggleKey(key)}
                      className={`text-xs rounded-full px-2.5 py-1 ring-2 ${ring} transition ${
                        active
                          ? `${pill} text-white shadow-sm`
                          : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 ring-slate-300 dark:ring-slate-700"
                      }`}
                      title={label}
                    >
                      {label}
                    </button>
                  );
                })}

                <button
                  onClick={() => {
                    setQuery("");
                    selectAll();
                    setSortBy("total");
                    setSortDir("desc");
                  }}
                  className="ml-auto inline-flex items-center gap-2 text-xs rounded-xl px-3 py-1.5 ring-1 ring-slate-300 dark:ring-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800"
                  title="Réinitialiser"
                >
                  <FaUndoAlt /> Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tableau */}
        <div className="px-5 pb-6 sm:px-6 mt-4">
          {/* bord à bord sur mobile pour choper le geste de scroll */}
          <div className="-mx-5 sm:mx-0">
            <div className="overflow-x-auto overscroll-x-contain touch-pan-x">
              <div className="min-w-[960px] rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100/80 dark:bg-slate-900/40">
                    <tr className="text-left text-slate-600 dark:text-slate-300">
                      <th className="px-4 py-3 sticky left-0 z-10 bg-slate-100/80 dark:bg-slate-900/40 backdrop-blur">
                        Cours
                      </th>
                      {STATUS_COLUMNS.map(({ key, label }) => (
                        <th key={key} className="px-3 py-3 text-center">
                          {label}
                        </th>
                      ))}
                      <th className="px-3 py-3 text-center">Total sélection</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {loading ? (
                      [...Array(4)].map((_, i) => (
                        <tr key={i} className="animate-pulse">
                          <td className="px-4 py-3 sticky left-0 z-10 bg-white dark:bg-slate-900">
                            <div className="h-4 w-40 rounded bg-slate-200 dark:bg-slate-800" />
                          </td>
                          {STATUS_COLUMNS.map(({ key }) => (
                            <td key={key} className="px-3 py-3 text-center">
                              <div className="h-4 w-8 mx-auto rounded bg-slate-200 dark:bg-slate-800" />
                            </td>
                          ))}
                          <td className="px-3 py-3 text-center">
                            <div className="h-4 w-10 mx-auto rounded bg-slate-200 dark:bg-slate-800" />
                          </td>
                        </tr>
                      ))
                    ) : filteredRows.length ? (
                      filteredRows.map((course, idx) => (
                        <tr
                          key={`${course.nom}-${idx}`}
                          className="bg-white/90 dark:bg-slate-900/40 hover:bg-slate-50 dark:hover:bg-slate-900/60 transition-colors"
                        >
                          <td className="px-4 py-3 sticky left-0 z-10 bg-white/90 dark:bg-slate-900/40 backdrop-blur font-medium text-slate-900 dark:text-slate-100">
                            {course.nom}
                          </td>
                          {STATUS_COLUMNS.map(({ key }) => (
                            <td
                              key={key}
                              className="px-3 py-3 text-center text-slate-800 dark:text-slate-100"
                            >
                              {Number(course[key] ?? 0) || "—"}
                            </td>
                          ))}
                          <td className="px-3 py-3 text-center font-semibold text-slate-900 dark:text-white">
                            {n(course._totalSelected)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={STATUS_COLUMNS.length + 2}
                          className="px-4 py-8 text-center text-slate-500 dark:text-slate-400"
                        >
                          Aucune donnée ne correspond aux filtres sélectionnés.
                        </td>
                      </tr>
                    )}

                    {!loading && filteredRows.length > 0 && (
                      <tr className="bg-slate-100/80 dark:bg-slate-900/40 font-semibold">
                        <td className="px-4 py-3 sticky left-0 z-10 bg-slate-100/80 dark:bg-slate-900/40 backdrop-blur">
                          Total
                        </td>
                        {STATUS_COLUMNS.map(({ key }) => (
                          <td key={key} className="px-3 py-3 text-center">
                            {n(columnTotals[key])}
                          </td>
                        ))}
                        <td className="px-3 py-3 text-center">
                          {n(totalSelectedAllRows)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
